import React, {useMemo, useState} from "react";
import {Button, Checkbox, Col, Row, Space, Tooltip} from "antd";
import {RightOutlined} from "@ant-design/icons";
import {
    AddIcon,
    DiamondIcon,
    DollarIcon,
    FlameIcon, InfoIcon,
    PriceListIcon,
    StorageBoxOutlineIcon,
    WishlistIcon
} from "@components/icons/Icons";
import {FmvPrice} from "@components/fmv-price";
import {KeyIssueBadge} from "@components/key-issue-badge";
import {IssueActions, QueryParms, Routes} from "@lib/constants";
import {Link} from "react-router-dom";
import {getMyCollectionUrlWithIssueId} from "@lib/Routes";
import {formatRawBucketGrade, getEbayTitleFromIssue} from "@lib/format";
import {ComicGradeSelect, getDefaultFmvDataFromIssue} from "@components/comic-grade-select";
import ebay_logo from "@images/ebay_logo.png";
import {showInfoModal} from "@lib/showModal";
import BookCover from "@components/book-cover";
import {RenderHtml} from "@components/html";


const IssuesListItemMobile = ({
      item,
      showEditMultiple,
      isSelected,
      toggleSelected,
      onClick,
      issueDate,
      showValueRibbon,
      allowUnlimited,
      navigate,
      windowDimensions,
      showListViewNoImages,
      wishlistIssueAdd,
      wishlistIssueRemove,
      setAddIssueId,
      isLoggedIn,
      setPriceListId,
      showIssueNotes,
      shopLink,
}) => {
    const [fmvData, setFmvData] = useState(getDefaultFmvDataFromIssue(item));
    const ebayTitle = useMemo(() => getEbayTitleFromIssue(item), [item]);

    return (
        <div className="grid-list-item" style={{width: '99%'}}>
            <Row className={"grid-list-item-inner"} gutter={[10,10]}>

                {(showEditMultiple && toggleSelected) && (
                    <Col span={1} className="col-cbx">
                        <Checkbox checked={isSelected} onChange={() => toggleSelected(item.id)}/>
                    </Col>
                )}

                <Col span={8}>
                    <div className={"col-image ant-row ant-row-center " + (showEditMultiple ? 'mar-left-5' : '')}>
                        <BookCover issue={item} className="list-book-cover" preview={true} />
                    </div>

                    {isLoggedIn && (
                        <Row className="issue-actions mar-top-5" justify="center" gutter={[5,5]}>
                            <Col span={24} className={"flex-align-center"}>
                                <Space>
                                    <Tooltip title={!item.in_wishlist
                                        ? IssueActions.WISHLIST.ADD
                                        : IssueActions.WISHLIST.REMOVE}
                                    >
                                        <Button className={`issueButton greyButton ${(item.in_wishlist
                                            ? 'selected'
                                            : '')}`}
                                                shape="circle" size="small"
                                                onClick={() => {
                                                    if (item.in_wishlist) {
                                                        wishlistIssueRemove([item.id]);
                                                    } else {
                                                        wishlistIssueAdd([item.id]);
                                                    }
                                                }}>
                                            <WishlistIcon/>
                                        </Button>
                                    </Tooltip>
                                    <Tooltip title={IssueActions.COLLECTION.ADD}>
                                        <Button
                                            className="issueButton greyButton"
                                            shape="circle" size="middle"
                                            onClick={() => {
                                                setAddIssueId(item.id);
                                            }}>
                                            <AddIcon/>
                                        </Button>
                                    </Tooltip>
                                    <Tooltip title={IssueActions.MARKETPLACE.BUY}>
                                        <a href={`${Routes.MARKETPLACE}/?${QueryParms.ISSUE_ID}=${item.id}`}>
                                            <Button
                                                className="issueButton greyButton"
                                                shape="circle" size="small">
                                                <DollarIcon className="dollar"/>
                                            </Button>
                                        </a>
                                    </Tooltip>
                                </Space>
                            </Col>

                        </Row>
                    )}

                </Col>

                <Col style={{display: 'flex', flexDirection: 'column'}}
                     span={(showEditMultiple && toggleSelected) ? 15 : 16}>
                    <Row gutter={[10,10]}>
                        <Col span={12} className="flex-col">
                            <div className="valueBadges">
                                {item.is_key && (
                                    <KeyIssueBadge keyInfo={item.key_info} windowDimensions={windowDimensions}
                                                   isLargeScreen={false}/>)}
                                {item.is_hot && (<div className="valueBadge hotIssue isActive"><FlameIcon/></div>)}
                                {item.is_rare && (<div className="valueBadge rareIssue isActive"><DiamondIcon/></div>)}
                            </div>
                        </Col>
                        <Col span={12} className="item-actions flex-align-right">
                            {item.total_in_collection > 0 && (
                                <Link to={getMyCollectionUrlWithIssueId(item.id)}>
                                    <span className="issues-owned">
                                        <StorageBoxOutlineIcon/><strong>&nbsp;:&nbsp;<span
                                        className="total-number">{item.total_in_collection}</span></strong>
                                    </span>
                                </Link>
                            )}
                        </Col>
                    </Row>

                    <Row className="item-title" style={{flexGrow: 1}}>
                        <Col span={24} className="flex-col">
                            <div className="break-spaces">
                                <div className="cursor-pointer titleLink font-16 font-bold">
                                    <a href={`/issue/${item.slug}`}>{item.series_title} {item.issue_number}</a>
                                </div>
                                <div className="font-14 font-normal">
                                    {(item.variant_name && item.variant_name !== '') ? item.variant_name : 'Regular'}
                                </div>
                                <div className="font-14 font-normal">
                                    {item.publisher} | {issueDate.getFullYear()}
                                </div>
                                {showIssueNotes && item.issue_notes &&  (
                                    <div style={{fontWeight: 'normal', paddingTop: 10}}><RenderHtml>{item.issue_notes}</RenderHtml></div>
                                )}
                            </div>

                        </Col>
                    </Row>

                    <Row className={"top-line-10 mar-bot-5"} gutter={10}>
                        <Col span={7}>
                            <ComicGradeSelect issue={item} onChange={setFmvData} />
                        </Col>
                        <Col span={10}>
                            <Button
                                onClick={() => setPriceListId(item.id)}
                                className={'greyButton text-light-blue rounded-3px '}
                            >Price Table</Button>
                        </Col>
                        <Col span={7}>
                            {shopLink ? (
                                <a href={shopLink.link_url} target="_blank" className={`ebay-link ${shopLink.className || ''}`}>
                                    <img src={shopLink.image} className={"ebay-logo"}/>
                                </a>
                            ) : (
                                <a
                                    href={`https://www.ebay.com/sch/63/i.html?_from=R40&_nkw=${ebayTitle}&mkcid=1&mkrid=711-53200-19255-0&siteid=0&campid=5339059464&toolid=20008&mkevt=1`}
                                    target="_blank" className="ebay-link"
                                >
                                    <img src={ebay_logo} className={"ebay-logo"}/>
                                </a>
                            )}
                        </Col>
                    </Row>


                    <Row className={"grade-boxes"} gutter={[10, 5]}>
                        <Col span={24}>
                            <Row>
                                <Col span={6} className={"raw-title"}>RAW</Col>
                                <Col span={10} className={"raw-text grade-text"}>
                                <span className={(fmvData.raw.stale ? 'fmv-price-stale' : '')}>
                                    {formatRawBucketGrade(fmvData.raw.grade)}
                                </span>
                                </Col>
                                <Col span={8} className={"raw-text value-text"}>
                                    {showValueRibbon ? (
                                        <FmvPrice value={fmvData.raw.value} stale={fmvData.raw.stale}/>
                                    ) : (
                                        <a className="upgrade-link" href="/plans/unlimited/">Upgrade</a>
                                    )}
                                </Col>
                            </Row>
                        </Col>
                        <Col span={24}>
                            <Row>
                                <Col span={6} className={"slab-title"}>SLAB</Col>
                                <Col span={10} className={"slab-text grade-text"}>
                                <span className={(fmvData.slabbed.stale ? 'fmv-price-stale' : '')}>
                                    {fmvData.slabbed.grade}
                                </span>
                                </Col>
                                <Col span={8} className={"slab-text value-text"}>
                                    {showValueRibbon ? (
                                        <FmvPrice value={fmvData.slabbed.value} stale={fmvData.slabbed.stale}/>
                                    ) : (
                                        <a className="upgrade-link" href="/plans/unlimited/">Upgrade</a>
                                    )}
                                </Col>
                            </Row>
                        </Col>
                    </Row>
                    <Row className={"top-line-10"} >
                        <Col span={24} >
                            <Button
                                href={`/issue/${item.slug}`}
                                className={'blueButton rounded-3px'}
                                style={{ width: '100%', height: 40, textAlign: 'left', fontWeight: 700, lineHeight: '40px', border: 0}}
                            >
                                Comic Details
                                <RightOutlined style={{float: 'right', lineHeight: 'inherit'}}/>
                            </Button>
                        </Col>
                    </Row>
                </Col>

            </Row>
        </div>
    );

};

export default IssuesListItemMobile;